import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import './Header.css';

const ROLE_LABELS: Record<'novagob_team' | 'jury' | 'public', { title: string; sub: string }> = {
  novagob_team: { title: 'NovaGob', sub: 'Equipo organizador' },
  jury: { title: 'Jurado', sub: 'Evaluación de candidaturas' },
  public: { title: 'Público', sub: 'Presenta y vota candidaturas' },
};

const Header: React.FC = () => {
  const {
    user,
    isNovagobTeam,
    isJury,
    isPublic,
    availableRoles,
    activeRole,
    setActiveRole,
    signOut,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [publicVotingEnabled, setPublicVotingEnabled] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // En la landing, cuando se hace scroll > 80px, activamos la variante
  // "scrolled" del header para que gane fondo oscuro y línea dorada
  // y se pueda leer sobre el contenido.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setNavOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    supabase
      .schema('premios')
      .from('platform_config')
      .select('public_voting_enabled')
      .single()
      .then(({ data }) => {
        setPublicVotingEnabled(!!data?.public_voting_enabled);
      });
  }, [user]);

  useEffect(() => {
    if (user) {
      supabase
        .schema('id')
        .from('users')
        .select('avatar')
        .eq('id', user.id)
        .single()
        .then(({ data }) => {
          setAvatarUrl(data?.avatar || null);
        });
    } else {
      setAvatarUrl(null);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const initials = user?.email ? user.email.slice(0, 2).toUpperCase() : '?';

  // En /auth no mostramos la cabecera: el formulario de login ya tiene su propia identidad visual.
  if (location.pathname === '/auth') {
    return null;
  }

  // En la landing pública (raíz del dominio) queremos una cabecera
  // transparente y elegante. `/inicio` se sigue reconociendo por si algún
  // enlace viejo todavía apunta allí y cae antes de la redirección.
  const isLanding =
    location.pathname === '/' || location.pathname === '/inicio';
  const headerClassName = [
    'header',
    isLanding ? 'header-transparent' : '',
    isLanding && scrolled ? 'header-scrolled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Logo: en la landing SIEMPRE imagen (con o sin sesión) para mantener la
  // estética minimalista. En el resto de la app con sesión, texto + label.
  const logoIsImage = isLanding || !user;

  // Link de destino del logo: con sesión y fuera de la landing → dashboard.
  // En cualquier otro caso → raíz (landing).
  const logoLinkTo = user && !isLanding ? '/dashboard' : '/';

  // Anclas de la landing que aparecen en el header solo cuando se ha hecho
  // scroll y el fondo se vuelve oscuro (espacio suficiente para leer). En
  // el tope de la página el header se queda transparente y sin anclas.
  const LANDING_ANCHORS: { id: string; label: string }[] = [
    { id: 'categorias', label: 'Categorías' },
    { id: 'bases', label: 'Bases' },
    { id: 'comite-jurado', label: 'Jurado' },
    { id: 'ganadores', label: 'Ganadores' },
    { id: 'actualidad', label: 'Actualidad' },
  ];

  const landingAnchorsNav = isLanding ? (
    <nav className="header-anchors" aria-label="Secciones de la página">
      {LANDING_ANCHORS.map((a) => (
        <a key={a.id} href={`#${a.id}`} className="header-anchor">
          {a.label}
        </a>
      ))}
    </nav>
  ) : null;

  return (
    <header className={headerClassName}>
      <div className="container header-content">
        <Link to={logoLinkTo} className="header-logo">
          {logoIsImage ? (
            <img
              src="/premios-logo.svg"
              alt="Premios NovaGob"
              className="header-logo-img"
            />
          ) : (
            <>
              <h1>Premios NovaGob</h1>
              <span className="dashboard-label">
                Dashboard {isNovagobTeam ? 'NovaGob' : isJury ? 'Jurado' : 'Público'}
              </span>
            </>
          )}
        </Link>

        {user ? (
          <>
            {/* Anclas a secciones de la landing — solo visibles con scroll */}
            {landingAnchorsNav}
            {/* En la landing ocultamos los nav links del dashboard: queda limpio
                como la versión de invitado, con el enlace sutil a novagob.org. */}
            {isLanding ? (
              <nav className="header-nav header-nav-guest">
                <a
                  href="https://novagob.org"
                  className="nav-link-subtle"
                  aria-label="Volver a novagob.org"
                >
                  <span aria-hidden="true" style={{ marginRight: 6 }}>←</span>
                  novagob.org
                </a>
              </nav>
            ) : (
              <nav className={`header-nav${navOpen ? ' open' : ''}`}>
                {isPublic && !isNovagobTeam && !isJury && publicVotingEnabled && (
                  <Link to="/votar" className="nav-link">Votaciones</Link>
                )}

                {!isNovagobTeam && (
                  <Link to="/dashboard" className="nav-link">
                    {isPublic && !isJury ? 'Mis Candidaturas' : 'Dashboard'}
                  </Link>
                )}

                {isNovagobTeam && (
                  <>
                    <Link
                      to="/dashboard"
                      className={`nav-link${/^\/(dashboard|manage-(roles|jury))/.test(location.pathname) ? ' active' : ''}`}
                    >
                      Gestión
                    </Link>
                    <Link to="/votaciones" className="nav-link">Votaciones</Link>
                    <Link
                      to="/manage-gala"
                      className={`nav-link${location.pathname === '/manage-gala' ? ' active' : ''}`}
                    >
                      Gala
                    </Link>
                    <Link to="/config" className="nav-link">Configuración</Link>
                  </>
                )}
              </nav>
            )}

            <div className="header-actions">
              <div className="header-user" ref={menuRef}>
                <button
                  className="profile-btn"
                  onClick={() => setMenuOpen((o) => !o)}
                  aria-label="Menu de perfil"
                  style={avatarUrl ? { padding: 0, overflow: 'hidden' } : undefined}
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt="Avatar"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    initials
                  )}
                </button>
                {menuOpen && (
                  <div className="profile-menu">
                    <div className="profile-menu-email">{user.email}</div>

                    {/* En la landing, acceso directo al dashboard del usuario */}
                    {isLanding && (
                      <>
                        <button
                          className="profile-menu-item profile-menu-item-primary"
                          onClick={() => {
                            setMenuOpen(false);
                            navigate('/dashboard');
                          }}
                        >
                          Ir al dashboard
                        </button>
                        <div className="profile-menu-divider" />
                      </>
                    )}

                    {availableRoles.length > 1 && (
                      <>
                        <div className="profile-menu-section-label">Rol activo</div>
                        {availableRoles.map((role) => {
                          const info = ROLE_LABELS[role];
                          const isActive = activeRole === role;
                          return (
                            <button
                              key={role}
                              type="button"
                              className={`role-toggle-row${isActive ? ' active' : ''}`}
                              onClick={() => {
                                if (isActive) return;
                                setActiveRole(role);
                                setMenuOpen(false);
                                // Forzamos recarga completa para que cualquier
                                // dashboard/página con datos ya cargados se
                                // reconstruya desde cero con el nuevo rol activo.
                                window.location.assign('/dashboard');
                              }}
                              aria-pressed={isActive}
                            >
                              <span className="role-toggle-row-label">
                                <span>{info.title}</span>
                                <span className="role-toggle-row-sub">{info.sub}</span>
                              </span>
                              <span
                                className={`role-toggle${isActive ? ' on' : ''}`}
                                aria-hidden="true"
                              />
                            </button>
                          );
                        })}
                        <div className="profile-menu-divider" />
                      </>
                    )}

                    <button
                      className="profile-menu-item"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate('/profile');
                      }}
                    >
                      Editar perfil
                    </button>
                    <button className="profile-menu-item" onClick={handleSignOut}>
                      Cerrar sesión
                    </button>
                  </div>
                )}
              </div>

              {/* Botón hamburguesa solo cuando hay nav de dashboard que mostrar */}
              {!isLanding && (
                <button
                  className="mobile-toggle"
                  onClick={() => setNavOpen((o) => !o)}
                  aria-label="Abrir menú"
                  aria-expanded={navOpen}
                >
                  {navOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
              )}
            </div>

            {navOpen && !isLanding && (
              <div
                className="nav-backdrop"
                onClick={() => setNavOpen(false)}
                aria-hidden="true"
              />
            )}
          </>
        ) : (
          <>
            {/* Anclas solo visibles con scroll en la landing */}
            {landingAnchorsNav}
            <nav className="header-nav header-nav-guest">
              <a
                href="https://novagob.org"
                className="nav-link-subtle"
                aria-label="Volver a novagob.org"
              >
                <span aria-hidden="true" style={{ marginRight: 6 }}>←</span>
                novagob.org
              </a>
              <Link to="/auth" className="nav-link">Iniciar Sesión</Link>
            </nav>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
